import { useEffect, useState } from 'react';

/**
 * SSR-safe media query hook. Returns `true` while the given CSS media query
 * matches the current viewport and updates when the match state changes.
 *
 * - On the server (no `window`), the initial value is `false` so the first
 *   client render matches the server render and avoids hydration mismatch.
 * - On mount, the hook immediately syncs to the real `matchMedia.matches`
 *   value so consumers get an accurate reading after paint.
 * - The listener is attached with `addEventListener('change', …)` and is
 *   cleaned up on unmount.
 *
 * @example
 * const isLg = useMediaQuery('(min-width: 1024px)');
 * if (!isLg) return <NarrowLayout />;
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mql = window.matchMedia(query);
    // Sync on mount in case the initial value drifted (e.g. SSR → hydration).
    setMatches(mql.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export default useMediaQuery;
